export interface PasswordOptions {
    requiredLength: number;
    requiredUniqueChars: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireDigit: boolean;
    requireNonAlphanumeric: boolean;
}

export interface ValidationMessages {
    required: string;
    minLength: string;
    uniqueChars: string;
    uppercase: string;
    lowercase: string;
    digit: string;
    nonAlphanumeric: string;
    confirmMismatch: string;
}

export class PasswordValidator {

    private messages: ValidationMessages;
    private passwordOptions: PasswordOptions;

    constructor(form: HTMLFormElement, private options: { passwordSelector: string, confirmPasswordSelector: string, rules: string }) {
        this.options.passwordSelector = this.options.passwordSelector ?? "#Password";
        this.options.confirmPasswordSelector = this.options.passwordSelector ?? "#ConfirmPassword";
        this.options.rules = this.options.rules ?? "#password-rules";
        this.initialize(form);        
    }
    // Génère la liste des règles non respectées
    getValidationErrors(password: string, confirmPassword: string | null): string[] {
        const rules: string[] = [];

        if (!password) {
            rules.push(this.messages.required);
        }

        if (password.length < this.passwordOptions.requiredLength) {
            rules.push(this.messages.minLength.replace('{0}', this.passwordOptions.requiredLength.toString()));
        }

        if (this.passwordOptions.requiredUniqueChars > 1 && new Set(password).size < this.passwordOptions.requiredUniqueChars) {
            rules.push(this.messages.uniqueChars.replace('{0}', this.passwordOptions.requiredUniqueChars.toString()));
        }

        if (this.passwordOptions.requireUppercase && !/[A-Z]/.test(password)) {
            rules.push(this.messages.uppercase);
        }

        if (this.passwordOptions.requireLowercase && !/[a-z]/.test(password)) {
            rules.push(this.messages.lowercase);
        }

        if (this.passwordOptions.requireDigit && !/\d/.test(password)) {
            rules.push(this.messages.digit);
        }

        if (this.passwordOptions.requireNonAlphanumeric && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            rules.push(this.messages.nonAlphanumeric);
        }

        if (confirmPassword && password !== confirmPassword) {
            rules.push(this.messages.confirmMismatch);
        }

        return rules;
    }
    // Affiche les règles non respectées
    updatePasswordRules(container: HTMLElement, password: string, confirmPassword: string | null): void {
        const rules: string[] = [];

        if (!password) {
            rules.push(this.messages.required);
            return;
        }

        if (password.length < this.passwordOptions.requiredLength) {
            rules.push(this.messages.minLength.replace('{0}', this.passwordOptions.requiredLength.toString()));
        }

        if (this.passwordOptions.requiredUniqueChars > 1 && new Set(password).size < this.passwordOptions.requiredUniqueChars) {
            rules.push(this.messages.uniqueChars.replace('{0}', this.passwordOptions.requiredUniqueChars.toString()));
        }

        if (this.passwordOptions.requireUppercase && !/[A-Z]/.test(password)) {
            rules.push(this.messages.uppercase);
        }

        if (this.passwordOptions.requireLowercase && !/[a-z]/.test(password)) {
            rules.push(this.messages.lowercase);
        }

        if (this.passwordOptions.requireDigit && !/\d/.test(password)) {
            rules.push(this.messages.digit);
        }

        if (this.passwordOptions.requireNonAlphanumeric && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            rules.push(this.messages.nonAlphanumeric);
        }

        if (confirmPassword && password !== confirmPassword) {
            rules.push(this.messages.confirmMismatch);
        }

        // Mettre à jour le <span> de validation
        container.innerHTML = rules.length > 0
            ? "<ul role=\"alert\" class=\"alert alert-danger\">"+rules.map(rule => `<li>${rule}</li>`).join('')+"</ul>"
            : '';

        //container.innerHTML = rules.length > 0
        //    ? `<div class="alert alert-danger"><ul>${rules.map(rule => `<li>${rule}</li>`).join('')}</ul></div>`
        //    : ``;
    }

    public initialize(form: HTMLFormElement) {
        const passwordInput = form.querySelector(this.options.passwordSelector) as HTMLInputElement;
        if (passwordInput) {
            
            const passwordOptions: PasswordOptions = {
                requiredLength: parseInt(passwordInput.getAttribute('data-val-passwordvalidation-requiredlength') || '6', 10),
                requiredUniqueChars: parseInt(passwordInput.getAttribute('data-val-passwordvalidation-requireduniquechars') || '1', 10),
                requireUppercase: (passwordInput.getAttribute('data-val-passwordvalidation-requireuppercase') || 'false') === 'true',
                requireLowercase: (passwordInput.getAttribute('data-val-passwordvalidation-requirelowercase') || 'false') === 'true',
                requireDigit: (passwordInput.getAttribute('data-val-passwordvalidation-requiredigit') || 'true') === 'true',
                requireNonAlphanumeric: (passwordInput.getAttribute('data-val-passwordvalidation-requirenonalphanumeric') || 'false') === 'true',
            };

            const messages: ValidationMessages = {
                required: passwordInput.getAttribute('data-val-passwordvalidation-messages-required') || 'Le mot de passe est requis.',
                minLength: passwordInput.getAttribute('data-val-passwordvalidation-messages-minlength') || 'Le mot de passe doit contenir au moins {0} caractères.',
                uniqueChars: passwordInput.getAttribute('data-val-passwordvalidation-messages-uniquechars') || 'Le mot de passe doit contenir au moins {0} caractères uniques.',
                uppercase: passwordInput.getAttribute('data-val-passwordvalidation-messages-uppercase') || 'Le mot de passe doit contenir au moins une lettre majuscule.',
                lowercase: passwordInput.getAttribute('data-val-passwordvalidation-messages-lowercase') || 'Le mot de passe doit contenir au moins une lettre minuscule.',
                digit: passwordInput.getAttribute('data-val-passwordvalidation-messages-digit') || 'Le mot de passe doit contenir au moins un chiffre.',
                nonAlphanumeric: passwordInput.getAttribute('data-val-passwordvalidation-messages-nonalphanumeric') || 'Le mot de passe doit contenir au moins un caractère spécial.',
                confirmMismatch: passwordInput.getAttribute('data-val-passwordvalidation-messages-confirmmismatch') || 'Le mot de passe et la confirmation ne correspondent pas.',
            };
            this.passwordOptions = passwordOptions;
            this.messages = messages;
        }
        this.initializeValidation(form);
    }

    initializeValidation(form: HTMLFormElement): void {

        // Activer la validation unobtrusive pour le formulaire chargé dynamiquement
        $.validator.unobtrusive.parse(form);
        const options = this.options;

        const passwordInput = form.querySelector(options.passwordSelector) as HTMLInputElement;
        const confirmPasswordInput = form.querySelector(options.confirmPasswordSelector) as HTMLInputElement;
        //const rulesContainer = form.querySelector(this.options.rules) as HTMLElement;
        //const validationSpan = form.querySelector('[data-valmsg-for="Password"]') as HTMLElement;

        if (passwordInput) {
            //const updateRules = () => {
            //    // Forcer la validation manuelle pour mettre à jour les messages
            //    const validator = $(form).validate();
            //    if (validator) {
            //        validator.element(options.passwordSelector);
            //    }
            //};
            const updateRules = () => {
                const validator = $(form).validate();
                if (validator) {
                    const password = passwordInput.value;
                    const confirmPassword = confirmPasswordInput?.value || null;
                    const errors = this.getValidationErrors(password, confirmPassword);

                    // Forcer l'affichage des erreurs dans le <span>
                    validator.showErrors({
                        'Password': errors.length > 0 ? errors.map(error => `<div>${error}</div>`).join('') : ''
                    });
                    validator.valid();
                }
            };

            //passwordInput.addEventListener('input', updateRules);
            if (confirmPasswordInput) {
                confirmPasswordInput.addEventListener('input', updateRules);
            }

            // Initialiser l'affichage des règles
            //updateRules();
        }

        //if (passwordInput && validationSpan) {
        //    const updateRules = () => {
        //        const password = passwordInput.value;
        //        const confirmPassword = confirmPasswordInput?.value || null;
        //        this.updatePasswordRules(validationSpan, password, confirmPassword);
        //    };

        //    passwordInput.addEventListener('input', updateRules);
        //    if (confirmPasswordInput) {
        //        confirmPasswordInput.addEventListener('input', updateRules);
        //    }

        //    // Initialiser l'affichage des règles
        //    updateRules();
        //}
        /*
        jQuery.validator.addMethod(
            'passwordvalidation',
            (value: string, element: HTMLElement, params: any): boolean => {
                const errors: string[] = [];

                const requiredLength = parseInt(params.requiredlength, 10);
                const requiredUniqueChars = parseInt(params.requireduniquechars, 10);
                const requireUppercase = params.requireuppercase === 'true';
                const requireLowercase = params.requirelowercase === 'true';
                const requireDigit = params.requiredigit === 'true';
                const requireNonAlphanumeric = params.requirenonalphanumeric === 'true';
                const confirmPasswordField = params.confirmpassword;

                if (!value) {
                    errors.push(params.messages.required);
                }

                if (value.length < requiredLength) {
                    errors.push(params.messages.minlength.replace('{0}', requiredLength.toString()));
                }

                if (requiredUniqueChars > 1 && new Set(value).size < requiredUniqueChars) {
                    errors.push(params.messages.uniquechars.replace('{0}', requiredUniqueChars.toString()));
                }

                if (requireUppercase && !/[A-Z]/.test(value)) {
                    errors.push(params.messages.uppercase);
                }

                if (requireLowercase && !/[a-z]/.test(value)) {
                    errors.push(params.messages.lowercase);
                }

                if (requireDigit && !/\d/.test(value)) {
                    errors.push(params.messages.digit);
                }

                if (requireNonAlphanumeric && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                    errors.push(params.messages.nonalphanumeric);
                }

                if (confirmPasswordField) {
                    const confirmPassword = (form.querySelector(`#${confirmPasswordField}`) as HTMLInputElement)?.value;
                    if (value !== confirmPassword) {
                        errors.push(params.messages.confirmmismatch);
                    }
                }

                (element as any).validationErrors = errors;
                return errors.length === 0;
            },
            (params: any, element: HTMLElement): string => {
                return (element as any).validationErrors?.join(' ') || params.message;
            }
        );
        */
        jQuery.validator.addMethod(
            'passwordvalidation',
            (value: string, element: HTMLElement, params: any): boolean => {
                const confirmPasswordField = params.confirmpassword;
                const confirmPassword = confirmPasswordField
                    ? (form.querySelector(`#${confirmPasswordField}`) as HTMLInputElement)?.value
                    : null;

                const errors = this.getValidationErrors(value, confirmPassword);
                (element as any).validationErrors = errors;

                return errors.length === 0;
            },
            (params: any, element: HTMLElement): string => {
                const errors = (element as any).validationErrors || [];
                return errors.map((error: string) => `<span>${error}</span>`).join('');
            }
        );

        jQuery.validator.unobtrusive.adapters.add(
            'passwordvalidation',
            ['requiredlength', 'requireduniquechars', 'requireuppercase', 'requirelowercase', 'requiredigit', 'requirenonalphanumeric', 'confirmpassword', 'messages'],
            (options: any): void => {
                options.rules['passwordvalidation'] = {
                    requiredlength: options.params.requiredlength,
                    requireduniquechars: options.params.requireduniquechars,
                    requireuppercase: options.params.requireuppercase,
                    requirelowercase: options.params.requirelowercase,
                    requiredigit: options.params.requiredigit,
                    requirenonalphanumeric: options.params.requirenonalphanumeric,
                    confirmpassword: options.params.confirmpassword,
                    messages: {
                        required: options.element.getAttribute('data-val-passwordvalidation-messages-required'),
                        minlength: options.element.getAttribute('data-val-passwordvalidation-messages-minlength'),
                        uniquechars: options.element.getAttribute('data-val-passwordvalidation-messages-uniquechars'),
                        uppercase: options.element.getAttribute('data-val-passwordvalidation-messages-uppercase'),
                        lowercase: options.element.getAttribute('data-val-passwordvalidation-messages-lowercase'),
                        digit: options.element.getAttribute('data-val-passwordvalidation-messages-digit'),
                        nonalphanumeric: options.element.getAttribute('data-val-passwordvalidation-messages-nonalphanumeric'),
                        confirmmismatch: options.element.getAttribute('data-val-passwordvalidation-messages-confirmmismatch'),
                    },
                };
                
                options.messages['passwordvalidation'] = () => this.getValidationErrors(options.element.value, null).map(error => `<div>${error}</div>`).join('');  //options.message;
                
            }
        );
    }
}